import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
  InputAdornment,
  ListItemButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import useUserFilters from "../../hooks/useUserFilters";
import { defaultFilters } from "../../defaultFilters";

const FilterModelManager = ({
  open,
  onClose,
  onSelectFilterModel,
  filterModel,
}) => {
  const [newModelName, setNewModelName] = useState("");
  const [localFilterModels, setLocalFilterModels] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const editInputRef = useRef(null);

  const { filters, loading, error, fetchFilters, saveFilter } =
    useUserFilters();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchFilters("value-betting", "data-grid-models-2");
  }, [fetchFilters]);

  useEffect(() => {
    let modelsToUse = [];
    if (filters && filters.content && filters.content.length > 0) {
      modelsToUse = filters.content;
    } else {
      modelsToUse = defaultFilters;
    }
    setLocalFilterModels(modelsToUse);

    const lastSelectedFilterId = localStorage.getItem("lastSelectedFilterId");
    if (lastSelectedFilterId) {
      const lastSelectedFilter = modelsToUse.find(
        (filter) => filter.id.toString() === lastSelectedFilterId
      );
      if (lastSelectedFilter) {
        onSelectFilterModel(lastSelectedFilter);
      }
    }
  }, [filters, onSelectFilterModel]);

  const updateFilters = useCallback(
    async (updatedModels) => {
      setIsSaving(true);
      setLocalFilterModels(updatedModels);
      try {
        await saveFilter({
          key: "data-grid-models-2",
          content: updatedModels,
          category: "value-betting",
        });
      } catch (error) {
        console.error("Error saving filters:", error);
      } finally {
        setIsSaving(false);
      }
    },
    [saveFilter]
  );

  const handleSaveNew = useCallback(async () => {
    const newModel = {
      id: Date.now(),
      name: newModelName,
      items: filterModel.items || [],
      logicOperator: filterModel.logicOperator || "and",
      sortModel: filterModel.sortModel || [],
    };
    console.log(newModel);
    const updatedModels = [...localFilterModels, newModel];
    await updateFilters(updatedModels);
    onSelectFilterModel(newModel);
    setNewModelName("");
  }, [
    filterModel,
    localFilterModels,
    newModelName,
    onSelectFilterModel,
    updateFilters,
  ]);

  const handleUpdate = useCallback(async () => {
    const updatedModels = localFilterModels.map((model) =>
      model.id === filterModel.id
        ? {
            ...filterModel,
            sortModel: filterModel.sortModel || [],
          }
        : model
    );
    await updateFilters(updatedModels);
  }, [filterModel, localFilterModels, updateFilters]);

  const handleDelete = useCallback(
    async (id) => {
      const updatedModels = localFilterModels.filter(
        (model) => model.id !== id
      );
      await updateFilters(updatedModels);
      if (filterModel.id === id) {
        onSelectFilterModel({ items: [] });
      }
    },
    [filterModel, localFilterModels, onSelectFilterModel, updateFilters]
  );

  const handleDuplicate = useCallback(
    async (model) => {
      const newModel = {
        ...model,
        id: Date.now(),
        name: `${model.name} Copy`,
        sortModel: model.sortModel || [],
      };
      const updatedModels = [...localFilterModels, newModel];
      await updateFilters(updatedModels);
    },
    [localFilterModels, updateFilters]
  );

  const onDragEnd = useCallback(
    async (result) => {
      if (!result.destination) return;

      const items = Array.from(localFilterModels);
      const [reorderedItem] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, reorderedItem);

      await updateFilters(items);
    },
    [localFilterModels, updateFilters]
  );

  const handleStartEditing = useCallback((id, name) => {
    setEditingId(id);
    setEditingName(name);
  }, []);

  const handleFinishEditing = useCallback(async () => {
    if (editingId !== null) {
      const updatedModels = localFilterModels.map((model) =>
        model.id === editingId ? { ...model, name: editingName } : model
      );
      await updateFilters(updatedModels);
      setEditingId(null);
      setEditingName("");
    }
  }, [editingId, editingName, localFilterModels, updateFilters]);

  const handleKeyPress = useCallback(
    (event) => {
      if (event.key === "Enter") {
        handleFinishEditing();
      }
    },
    [handleFinishEditing]
  );

  useEffect(() => {
    if (editingId !== null && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  if (error) {
    console.error("Error loading filters:", error);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
    >
      <DialogTitle>Manage Filter Models</DialogTitle>
      <DialogContent sx={{ padding: isMobile ? 1 : 2 }}>
        <TextField
          autoFocus
          margin="dense"
          label="New Filter Model Name"
          fullWidth
          value={newModelName}
          onChange={(e) => setNewModelName(e.target.value)}
          size={isMobile ? "small" : "medium"}
        />
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="filter-models">
            {(provided) => (
              <List
                {...provided.droppableProps}
                ref={provided.innerRef}
                dense={isMobile}
              >
                {localFilterModels.map((model, index) => (
                  <Draggable
                    key={model.id}
                    draggableId={model.id.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <ListItemButton
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        onClick={() => onSelectFilterModel(model)}
                        selected={filterModel.id === model.id}
                        sx={{ paddingY: isMobile ? 0.5 : 1 }}
                      >
                        <IconButton
                          {...provided.dragHandleProps}
                          size={isMobile ? "small" : "medium"}
                        >
                          <DragIndicatorIcon
                            fontSize={isMobile ? "small" : "medium"}
                          />
                        </IconButton>
                        {editingId === model.id ? (
                          <TextField
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onBlur={handleFinishEditing}
                            onKeyPress={handleKeyPress}
                            inputRef={editInputRef}
                            size={isMobile ? "small" : "medium"}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleFinishEditing();
                                    }}
                                    edge="end"
                                    size={isMobile ? "small" : "medium"}
                                  >
                                    <CheckIcon
                                      fontSize={isMobile ? "small" : "medium"}
                                    />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        ) : (
                          <ListItemText
                            sx={{ pr: 10 }}
                            primary={model.name}
                            primaryTypographyProps={{
                              fontSize: isMobile ? "0.9rem" : "1rem",
                            }}
                          />
                        )}
                        <ListItemSecondaryAction
                          sx={{
                            display: "flex",
                            gap: isMobile ? "4px" : "16px",
                          }}
                        >
                          {["edit", "duplicate", "delete"].map((action) => (
                            <IconButton
                              key={action}
                              edge="end"
                              aria-label={action}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (action === "edit")
                                  handleStartEditing(model.id, model.name);
                                if (action === "duplicate")
                                  handleDuplicate(model);
                                if (action === "delete") handleDelete(model.id);
                              }}
                              size={isMobile ? "small" : "medium"}
                            >
                              {action === "edit" && (
                                <EditIcon
                                  fontSize={isMobile ? "small" : "medium"}
                                />
                              )}
                              {action === "duplicate" && (
                                <FileCopyIcon
                                  fontSize={isMobile ? "small" : "medium"}
                                />
                              )}
                              {action === "delete" && (
                                <DeleteIcon
                                  fontSize={isMobile ? "small" : "medium"}
                                />
                              )}
                            </IconButton>
                          ))}
                        </ListItemSecondaryAction>
                      </ListItemButton>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions
        sx={{
          flexWrap: "wrap",
          justifyContent: "center",
          padding: isMobile ? 1 : 2,
        }}
      >
        <Button
          onClick={() =>
            onSelectFilterModel({
              items: [],
              maxOdds: "10",
              bookieCount: "3",
              hoursToEvent: "48",
              selectedSport: null,
              selectedBookmakers: [],
            })
          }
          size={isMobile ? "small" : "medium"}
        >
          Deselect Filter
        </Button>
        <Button onClick={onClose} size={isMobile ? "small" : "medium"}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveNew}
          disabled={!newModelName || isSaving}
          size={isMobile ? "small" : "medium"}
        >
          Save As New
        </Button>
        {filterModel.name && (
          <Button
            onClick={handleUpdate}
            disabled={isSaving}
            size={isMobile ? "small" : "medium"}
          >
            Update Current
          </Button>
        )}
      </DialogActions>
      {isSaving && (
        <CircularProgress
          size={24}
          style={{ position: "absolute", top: "50%", left: "50%" }}
        />
      )}
    </Dialog>
  );
};

export default FilterModelManager;
